import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API, formatINR, formatDate, daysUntil } from "../lib/api";
import { ArrowLeft, Pill, Package, Calendar, MapPin, Building2, Barcode, Info, ExternalLink, FlaskConical, Repeat } from "lucide-react";

export default function MedicineDetail() {
  const { id } = useParams();
  const [med, setMed] = useState(null);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const [equivalents, setEquivalents] = useState([]);
  const [equivLoading, setEquivLoading] = useState(false);
  const [strictMatch, setStrictMatch] = useState(true); // start with salt + strength

  useEffect(() => {
    API.get(`/medicines/${id}`).then((r) => setMed(r.data)).catch((e) => setErr(e?.message || "Error"));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setInfoLoading(true);
    API.get(`/medicines/${id}/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ summary: "", source_url: "", error: "Could not fetch info" }))
      .finally(() => setInfoLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setEquivLoading(true);
    API.get(`/medicines/${id}/equivalents`, { params: { strict: strictMatch, in_stock_only: true } })
      .then((r) => setEquivalents(r.data.equivalents || []))
      .catch(() => setEquivalents([]))
      .finally(() => setEquivLoading(false));
  }, [id, strictMatch]);

  if (err) return <div className="p-8 text-red-600">{err}</div>;
  if (!med) return <div className="p-8 text-slate-500">Loading…</div>;

  const days = daysUntil(med.expiry_date);
  const stockStatus = med.quantity === 0 ? "danger" : med.quantity <= (med.low_stock_threshold || 10) ? "warning" : "success";

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-6xl" data-testid="medicine-detail-page">
      <Link to="/stock" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#002FA7] mb-4" data-testid="back-to-stock">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Stock
      </Link>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 sm:p-6 md:p-8 flex items-start gap-6 flex-wrap border-b border-slate-200">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#1D4ED8] text-white flex items-center justify-center shrink-0">
            <Pill className="w-8 h-8" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1">{med.company}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight" data-testid="medicine-name">{med.name}</h1>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className={`tag tag-${stockStatus}`}><Package className="w-3 h-3" /> {med.quantity} in stock</span>
              {days !== null && days <= 90 && <span className={`tag ${days < 30 ? "tag-danger" : "tag-warning"}`}><Calendar className="w-3 h-3" /> {days} days to expiry</span>}
              <span className="tag tag-info"><MapPin className="w-3 h-3" /> {med.location || "No location"}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="overline">MRP</div>
            <div className="font-display text-4xl font-bold text-[#002FA7]" data-testid="medicine-mrp">{formatINR(med.mrp)}</div>
            <div className="text-xs text-slate-500 mt-1">Purchase: {formatINR(med.purchase_price)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-slate-200">
          <KV label="Batch Number" value={med.batch_number} icon={Barcode} mono />
          <KV label="Manufacturing" value={formatDate(med.manufacturing_date)} icon={Calendar} />
          <KV label="Expiry" value={formatDate(med.expiry_date)} icon={Calendar} />
          <KV label="Barcode" value={med.barcode || "—"} icon={Barcode} mono />
        </div>

        <div className="p-4 sm:p-6 md:p-8">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <Info className="w-4 h-4" /> What is this medicine used for?
          </h3>
          {infoLoading ? (
            <div className="text-sm text-slate-500 italic">Fetching info from Wikipedia…</div>
          ) : info && info.summary ? (
            <div data-testid="medicine-info" className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{info.summary}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
                {info.source_url && (
                  <a href={info.source_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-[#002FA7] font-semibold hover:underline"
                     data-testid="medicine-info-source">
                    Source: Wikipedia <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <ExtLinks name={med.name} />
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="text-sm text-slate-500 italic">
                {info?.note || info?.error || "No reference info available on Wikipedia — try one of the searches below."}
              </div>
              <ExtLinks name={med.name} />
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 md:p-8 border-t border-slate-100">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-[#10B981]" /> Salt Composition
          </h3>
          {Array.isArray(med.composition) && med.composition.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="composition-list">
              {med.composition.map((c, i) => (
                <span key={i} data-testid={`composition-chip-${i}`}
                      className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1 text-sm">
                  <span className="font-semibold">{c.salt}</span>
                  {c.strength != null && c.strength !== "" && (
                    <span className="text-emerald-700">· {c.strength}{c.unit || ""}</span>
                  )}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic" data-testid="composition-empty">
              No composition recorded. Edit this medicine to add the active salts so you can find equivalents.
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 md:p-8 border-t border-slate-100">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Repeat className="w-4 h-4 text-[#002FA7]" /> Equivalent Medicines (in stock)
            </h3>
            <div className="inline-flex bg-slate-100 rounded-lg p-1 text-xs">
              <button data-testid="equiv-strict-on" onClick={() => setStrictMatch(true)}
                      className={`px-3 py-1.5 rounded-md ${strictMatch ? "bg-white shadow font-semibold" : "text-slate-600"}`}>
                Salt + Strength
              </button>
              <button data-testid="equiv-strict-off" onClick={() => setStrictMatch(false)}
                      className={`px-3 py-1.5 rounded-md ${!strictMatch ? "bg-white shadow font-semibold" : "text-slate-600"}`}>
                Same Salt
              </button>
            </div>
          </div>
          {equivLoading ? (
            <div className="text-sm text-slate-500 italic">Looking up equivalents…</div>
          ) : (!med.composition || med.composition.length === 0) ? (
            <div className="text-sm text-slate-500 italic">Add a salt to this medicine to find equivalents.</div>
          ) : equivalents.length === 0 ? (
            <div className="text-sm text-slate-500 italic" data-testid="equiv-empty">
              No in-stock equivalents found{strictMatch ? " (with the same strength)" : ""}. Try the other matching mode above.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="equivalents-table">
                <thead><tr><th>Medicine</th><th>Company</th><th>Composition</th><th className="text-right">MRP</th><th className="text-right">Stock</th><th></th></tr></thead>
                <tbody>
                  {equivalents.map((e) => (
                    <tr key={e.id} data-testid={`equiv-row-${e.id}`}>
                      <td className="font-medium">{e.name}</td>
                      <td className="text-slate-600">{e.company}</td>
                      <td className="text-xs text-slate-600">
                        {(e.composition || []).map((c, i) => (
                          <span key={i} className="mr-1.5">
                            {c.salt}{c.strength != null && c.strength !== "" ? ` ${c.strength}${c.unit || ""}` : ""}
                            {i < (e.composition.length - 1) ? " +" : ""}
                          </span>
                        ))}
                      </td>
                      <td className="text-right font-semibold">{formatINR(e.mrp)}</td>
                      <td className="text-right">
                        <span className={`tag ${e.quantity === 0 ? "tag-danger" : e.quantity <= 10 ? "tag-warning" : "tag-success"}`}>{e.quantity}</span>
                      </td>
                      <td className="text-right">
                        <Link to={`/medicine/${e.id}`} className="text-xs text-[#002FA7] font-semibold hover:underline">View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 md:p-8 border-t border-slate-100">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Branch-wise Availability
          </h3>
          {med.availability && med.availability.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>Branch</th><th>Batch</th><th className="text-right">Quantity</th><th>Expiry</th></tr></thead>
                <tbody>
                  {med.availability.map((a, i) => (
                    <tr key={i}>
                      <td className="font-medium">{a.branch_name}</td>
                      <td className="font-mono text-xs">{a.batch_number}</td>
                      <td className="text-right">
                        <span className={`tag ${a.quantity === 0 ? "tag-danger" : a.quantity <= 10 ? "tag-warning" : "tag-success"}`}>{a.quantity}</span>
                      </td>
                      <td>{formatDate(a.expiry_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="text-sm text-slate-500">Only available in this branch.</div>}
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, icon: Icon, mono }) {
  return (
    <div className="p-4 sm:p-5 border-b border-r border-slate-100 md:[&:nth-child(4n)]:border-r-0 [&:nth-child(2n)]:border-r-0 md:[&:nth-child(2n)]:border-r md:last:border-r-0">
      <div className="flex items-center gap-2 overline mb-1.5">
        <Icon className="w-3 h-3" />{label}
      </div>
      <div className={`text-sm font-semibold text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}


function ExtLinks({ name }) {
  const q = encodeURIComponent(`${name} medicine uses dosage side effects`);
  const links = [
    { label: "Google", href: `https://www.google.com/search?q=${q}`, color: "text-blue-600" },
    { label: "1mg", href: `https://www.1mg.com/search/all?name=${encodeURIComponent(name)}`, color: "text-amber-700" },
    { label: "Drugs.com", href: `https://www.drugs.com/search.php?searchterm=${encodeURIComponent(name)}`, color: "text-purple-700" },
    { label: "PubMed", href: `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(name)}`, color: "text-emerald-700" },
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="medicine-ext-search">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 self-center mr-1">Search on</span>
      {links.map((l) => (
        <a key={l.label} href={l.href} target="_blank" rel="noreferrer"
           data-testid={`ext-search-${l.label.toLowerCase()}`}
           className={`inline-flex items-center gap-1 text-xs font-semibold hover:underline ${l.color}`}>
          {l.label} <ExternalLink className="w-3 h-3" />
        </a>
      ))}
    </div>
  );
}